import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth, UserDetails } from '../../../../contexts/AuthContext';
import { CartItem } from '../QuickSaleScreen';
import type { SellerSnapshot } from '../../../../services/sellerInfoService';
import axios from 'axios';
import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';

// Fiş yazdırma servisi için tip tanımlamaları
interface PrintReceiptServiceProps {
  companyId: string | null;
  screenId: string | null;
  screenSettings?: any; // ✅ SQL'den çekilen ekran ayarları (PrintingSettingsJson)
  /**
   * ✅ PR2 (Invoice Seller Snapshot Refactor):
   * Backend tarafından çözülen satıcı snapshot. Franchise şubeler için kritik:
   * fiş başlığında ana firma yerine doğru franchise firma adı/VKN/adresi
   * görünmesi için kullanılır. `receiptDisplaySettings.companyName` vb.
   * kullanıcı tarafından özellikle doldurulmuşsa onu önceler (kullanıcı tercihi);
   * boşsa seller snapshot'a düşer.
   */
  seller?: SellerSnapshot | null;
  onFetchSettingsError?: (error: string) => void;
  onPrintError?: (error: string) => void;
  onPrintSuccess?: (message: string) => void;
  lazyLoad?: boolean; // Ayarların sadece ihtiyaç olduğunda yüklenmesi için
}

// QuickSaleScreenSettingsModal.tsx dosyasındaki ReceiptSettings arayüzü ile aynı olmalı
interface ReceiptDisplaySettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumberResetValue: number;
  resetAtEndOfDay: boolean;
  printCopyCount: number; // Birden fazla kopya yazdırma sayısı
  productGrouping: string; 
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number; // Bu ve altındakiler yerel servis tarafından kullanılabilir
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  // Logo başlık ayarları (boyutlar frontend'de - local service sadece uygular)
  headerType?: 'text' | 'logo';
  logoUrl?: string;
  logoMaxWidthPx?: number;  // 80mm = 384px. Logo max genişlik (örn: 384)
  logoMaxHeightPx?: number; // Logo max yükseklik (varsayılan: 150)
}

// ✅ YENİ: Alt ürün tipi (opsiyonlar ve menü öğeleri için)
export interface ReceiptSubItem {
  name: string;
  quantity: number;
  isExtraOption?: boolean;  // Ekstra opsiyon mu?
}

// Yazdırılacak fiş verisi için tip tanımlaması
export interface ReceiptDataItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  vatRate?: number;
  subItems?: ReceiptSubItem[];  // ✅ YENİ: Alt ürünler (menü öğeleri ve opsiyonlar)
}

export interface ReceiptData {
  invoiceNumber: string;
  date: string;
  time: string;
  customerName: string;
  items: ReceiptDataItem[];
  subtotal: number;
  vatTotal: number;
  discountTotal: number;
  grandTotal: number;
  paymentMethod: string;
  cashierName: string;
  companyInfo: {
    name: string;
    address: string;
    taxNumber: string;
    phone: string;
  };
  showVatInfo: boolean;
  showReceiptHeader: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumber?: number;
  resetAtEndOfDay?: boolean;
  printCopyCount?: number; // Birden fazla kopya yazdırma sayısı
  productGrouping?: string;
  standardFooterNote?: string;
  thankYouMessage?: string;
  receiptFormat?: {
    receiptWidth?: number;
    characterLength?: number;
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
  };
  remainingBalance?: number; // YENİ: Cari hesabın işlem sonrası kalan bakiyesi (ön ödemeli sistem için)
  htmlTemplate?: string; // YENİ: HTML şablonu
  orderNumber?: string; // ✅ YENİ: Sipariş numarası (HS-XXXXXX formatında)
  tableName?: string;
  headerType?: 'text' | 'logo';
  logoUrl?: string;
  logoMaxWidthPx?: number;  // Frontend'de tanımlı - local service sadece uygular
  logoMaxHeightPx?: number;
  customerNote?: string;
  // ✅ YENİ: Fiyatlar zaten KDV dahil mi? true ise ön ödemeli KDV yeniden ekleme
  // bloğu ATLANIR (yeni ön ödemeli hızlı satış fiyatları KDV dahil gelir).
  pricesAreVatInclusive?: boolean;
}

// Yazıcı ve Fiş Görüntü Ayarları için birleşik tip
interface ScreenPrintSettings {
  definedPrinters?: { id: string; name: string }[];
  printingMode?: 'single' | 'category';
  defaultReceiptPrinter?: { id: string; name: string } | null;
  categoryPrinterAssignments?: { [categoryId: string]: string | null };
  // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel) — fiş her iki yazıcıya da gider
  categoryPrinterAssignments2?: { [categoryId: string]: string | null };
  receiptDisplaySettings?: ReceiptDisplaySettings; // YENİ: Fiş görüntüleme ayarları
}

interface PrintReceiptServiceReturn {
  printReceipt: (
    baseReceiptData: Omit<ReceiptData, 'cashierName' | 'companyInfo' | 'showReceiptHeader' | 'showVatInfo' | 'showDiscountInfo' | 'productGrouping' | 'standardFooterNote' | 'thankYouMessage' | 'receiptFormat' | 'items' | 'vatTotal'>,
    cartItems: CartItem[],
    /**
     * ✅ YENİ: Yazdırma davranışı opsiyonları.
     * - forceSinglePrinter=true: Kategori bazlı modda OLSA BİLE, ürünleri kategoriye göre
     *   ayırıp mutfak yazıcılarına dağıtmaz; tek bir adisyon/fiş yazıcısına basar.
     */
    options?: { forceSinglePrinter?: boolean }
  ) => Promise<void>;
}

// Yerel yazıcı servisi endpoint (varsayılan, screenSettings'den override edilebilir)
const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';

// Fiş HTML şablonunu oluşturan fonksiyon
const createReceiptHtml = (receiptData: ReceiptData): string => {
  // Fiş numarası gösterim kısmını hazırla
  let receiptNumberHTML = '';
  if (receiptData.showReceiptNumber && receiptData.receiptNumber) {
    receiptNumberHTML = `<div class="receipt-number">Fiş Sırası: ${receiptData.receiptNumber}</div>`;
  }

  // Ön ödemeli sistem etkinse fiyatları KDV dahil göstermek için
  let items = [...receiptData.items];
  
  // Ön ödemeli sistemde fiyatları KDV dahil olarak yeniden hesapla.
  // ⚠️ pricesAreVatInclusive=true ise fiyatlar ZATEN KDV dahil gelir (yeni ön ödemeli
  // hızlı satış); tekrar KDV eklenirse tutar şişer (ör. 390 → 420). Bu durumda atla.
  if (receiptData.remainingBalance !== undefined && !receiptData.pricesAreVatInclusive) {
    items = items.map(item => {
      const vatRate = item.vatRate || 0;
      const kdvDahilBirimFiyat = item.unitPrice * (1 + (vatRate / 100));
      const kdvDahilToplamFiyat = item.totalPrice * (1 + (vatRate / 100));
      
      return {
        ...item,
        unitPrice: kdvDahilBirimFiyat,
        totalPrice: kdvDahilToplamFiyat
      };
    });
  }

  // Fiş HTML şablonunu oluştur
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Fiş Yazdır</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 9pt;
          width: 80mm;
          margin: 0;
          padding: 2mm 4mm 3mm 4mm;
          box-sizing: border-box;
          line-height: 1.1;
        }
        .header {
          text-align: center;
          margin-bottom: 3px;
          padding-top: 0;
        }
        .header h2 {
          margin: 0;
          margin-bottom: 2px;
        }
        .header img {
          max-width: 50mm;
          max-height: 18mm;
          margin-left: -3mm; /* Logo sola kaydırma */
          image-rendering: -webkit-optimize-contrast;
        }
        .company-info {
          text-align: center;
          margin-bottom: 3px;
          font-size: 8pt;
        }
        .company-info p {
          margin: 0 0 1px 0; /* Paragraf aralarındaki boşluğu azalttım */
        }
        .invoice-details {
          margin-bottom: 3px;
          font-size: 8pt;
        }
        .invoice-details p {
          margin: 0 0 1px 0; /* Paragraf aralarındaki boşluğu azalttım */
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
          margin-top: 2px;
          table-layout: fixed; /* ✅ Sabit tablo genişliği */
        }
        .items-table th, .items-table td {
          text-align: left;
          padding: 1px 0; /* 2px'ten 1px'e düşürüldü */
        }
        .items-table th {
          border-bottom: 1px solid #ddd;
        }
        .summary {
          margin-top: 3px;
          text-align: right;
          font-size: 8pt;
        }
        .summary p {
          margin: 0 0 1px 0; /* Paragraf aralarındaki boşluğu azalttım */
        }
        .footer {
          margin-top: 5px;
          text-align: center;
          font-size: 8pt;
          border-top: 1px dashed #ddd;
          padding-top: 3px;
        }
        .footer p {
          margin: 0 0 1px 0; /* Paragraf aralarındaki boşluğu azalttım */
        }
        .bold {
          font-weight: bold;
        }
        .center {
          text-align: center;
        }
        .right {
          text-align: right;
        }
        .sub-item td {
          border: none;
          padding-top: 2px;
          padding-bottom: 2px;
          line-height: 1.3;
          font-weight: normal;
        }
        .receipt-number {
          font-size: 10pt;
          text-align: center;
          margin-bottom: 8px;
          font-weight: bold;
          border: 1px solid #ddd;
          padding: 3px;
          border-radius: 4px;
          background-color: #f9f9f9;
        }
        .order-number {
          font-size: 20pt;
          text-align: center;
          margin-bottom: 10px;
          margin-top: 8px;
          font-weight: bold;
          letter-spacing: 3px;
          color: #000;
        }
        .customer-note {
          font-size: 12pt;
          text-align: center;
          margin-bottom: 8px;
          margin-top: 5px;
          font-weight: bold;
          color: #000;
          padding: 3px 0;
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
        }
        .remaining-balance {
          font-weight: bold;
          border-top: 1px dotted #ccc;
          padding-top: 3px;
          margin-top: 3px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${receiptData.headerType === 'logo' && receiptData.logoUrl ? 
          `<img src="${receiptData.logoUrl}" alt="Logo" />` : 
          `<h2 style="margin: 0">${receiptData.companyInfo?.name || 'AQUASOFT'}</h2>`
        }
      </div>
      
      ${receiptData.headerType === 'logo' && receiptData.logoUrl ? '' : `
      <div class="company-info">
        ${receiptData.companyInfo?.address ? `<p>${receiptData.companyInfo.address}</p>` : ''}
        ${receiptData.companyInfo?.phone ? `<p>Tel: ${receiptData.companyInfo.phone}</p>` : ''}
        ${receiptData.companyInfo?.taxNumber ? `<p>VKN: ${receiptData.companyInfo.taxNumber}</p>` : ''}
      </div>
      `}
      
      ${receiptData.customerNote ? `<div class="customer-note">${receiptData.customerNote}</div>` : ''}
      
      ${receiptData.orderNumber ? `<div class="order-number">SİPARİŞ : ${receiptData.orderNumber}</div>` : ''}
      ${receiptData.tableName ? `<div class="order-number">MASA : ${receiptData.tableName}</div>` : ''}
      
      ${receiptNumberHTML}
      
      <div class="invoice-details">
        <p><span class="bold">Fiş No:</span> ${receiptData.invoiceNumber}</p>
        <p><span class="bold">Tarih:</span> ${receiptData.date} ${receiptData.time}</p>
        <p><span class="bold">Cari:</span> ${receiptData.customerName}</p>
        ${receiptData.cashierName ? `<p><span class="bold">Kasiyer:</span> ${receiptData.cashierName}</p>` : ''}
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 45%;">Ürün</th>
            <th class="right" style="width: 12%;">Adet</th>
            <th class="right" style="width: 20%;">Fiyat</th>
            <th class="right" style="width: 23%;">Tutar</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td class="right">${item.quantity}</td>
              <td class="right">${item.unitPrice.toFixed(2)}</td>
              <td class="right">${item.totalPrice.toFixed(2)}</td>
            </tr>
            ${item.subItems && item.subItems.length > 0 ? item.subItems.map(subItem => `
              <tr class="sub-item">
                <td style="padding-left: 8px; font-size: 8pt; color: #000;">
                  - ${subItem.name}
                </td>
                <td class="right" style="font-size: 8pt; color: #000;">${subItem.quantity}</td>
                <td class="right" style="font-size: 8pt; color: #000;">--</td>
                <td class="right" style="font-size: 8pt; color: #000;">--</td>
              </tr>
            `).join('') : ''}
          `).join('')}
        </tbody>
      </table>
      
      <div class="summary">
        ${receiptData.discountTotal > 0 ? `<p>İndirim: ${receiptData.discountTotal.toFixed(2)} TL</p>` : ''}
        ${/* ✅ GİZLENDİ: Ara Toplam ve KDV Toplam şimdilik gizli
        receiptData.remainingBalance === undefined ? `<p>Ara Toplam: ${receiptData.subtotal.toFixed(2)} TL</p>` : ''
        receiptData.remainingBalance === undefined ? `<p>KDV Toplam: ${receiptData.vatTotal.toFixed(2)} TL</p>` : ''
        */ ''}
        <p class="bold">Genel Toplam: ${receiptData.grandTotal.toFixed(2)} TL</p>
        ${receiptData.remainingBalance !== undefined ? 
          `<p class="remaining-balance">Kalan Bakiye: ${receiptData.remainingBalance.toFixed(2)} TL</p>` : ''}
      </div>
      
      <div class="footer">
        ${receiptData.remainingBalance === undefined ? 
          `<p><span class="bold">Ödeme Şekli:</span> ${receiptData.paymentMethod}</p>` : ''}
        ${receiptData.standardFooterNote ? `<p>${receiptData.standardFooterNote}</p>` : ''}
        ${receiptData.thankYouMessage ? `<p>${receiptData.thankYouMessage}</p>` : ''}
      </div>
    </body>
    </html>
  `;
};

// Sınama sayfası HTML şablonunu oluşturan fonksiyon
const createTestPageHtml = (printerName: string): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Yazıcı Sınama Sayfası</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 10pt;
          width: 80mm; /* 80mm genişlik */
          margin: 0;
          padding: 2mm 5mm 5mm 5mm;
          box-sizing: border-box;
          line-height: 1.1;
        }
        .header {
          text-align: center;
          border-bottom: 1px solid black;
          padding-bottom: 10px;
          margin-bottom: 10px;
        }
        .info {
          margin-bottom: 15px;
        }
        .test-area {
          border: 1px solid black;
          padding: 10px;
          margin-bottom: 15px;
        }
        .footer {
          text-align: center;
          border-top: 1px solid black;
          padding-top: 10px;
        }
        pre {
          font-family: 'Courier New', monospace;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>YAZICI SINAMA SAYFASI</h1>
        <p>GTS Yazdırma Servisi</p>
      </div>
      
      <div class="info">
        <p><strong>Yazıcı:</strong> ${printerName}</p>
        <p><strong>Tarih:</strong> ${new Date().toLocaleString('tr-TR')}</p>
      </div>
      
      <div class="test-area">
        <h3>KARAKTER TESTİ</h3>
        <p>ABCDEFGHJKLMNOPQRSTUVWXYZ</p>
        <p>abcdefghijklmnopqrstuvwxyz</p>
        <p>0123456789</p>
        <p>!@#$%^&*()_+-=</p>
      </div>
      
      <div class="test-area">
        <h3>TÜRKÇE KARAKTER TESTİ</h3>
        <p>çÇ ğĞ ıİ öÖ şŞ üÜ</p>
        <p>ÖZEL TÜRKÇE KARAKTERLER</p>
        <p>Şükrü Şahin</p>
        <p>Güzel çiçekler</p>
        <p>Ölçü İğnesi</p>
        <p>Ünlü Şapkası</p>
      </div>
      
      <div class="footer">
        <p>Bu bir sınama sayfasıdır.</p>
        <p>Eğer bu metni net şekilde okuyabiliyorsanız,</p>
        <p>yazıcınız başarıyla yapılandırılmıştır.</p>
      </div>
    </body>
    </html>
  `;
};

// Test sayfası yazdırma fonksiyonu
export const printTestPage = async (printerName: string, localServiceUrl?: string): Promise<void> => {
  const baseUrl = localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;
  try {
    const htmlTemplate = createTestPageHtml(printerName);
    await axios.post(`${baseUrl}/api/sinamaSayfasiYazdir`, {
      printerName,
      htmlTemplate
    });
    console.log(`%c[PrintReceiptService] Sınama sayfası ${printerName} yazıcısına gönderildi.`, "color: green;");
    return Promise.resolve();
  } catch (error: any) {
    console.error(`%c[PrintReceiptService] Sınama sayfası yazdırma hatası:`, "color: red;", error);
    return Promise.reject(error);
  }
};

export const usePrintReceipt = ({
  companyId,
  screenId,
  screenSettings: externalScreenSettings, // ✅ SQL'den gelen ayarlar (prop olarak)
  seller, // ✅ PR2: Backend resolver'dan satıcı snapshot
  onFetchSettingsError,
  onPrintError,
  onPrintSuccess,
  lazyLoad = true // Varsayılan olarak ayarları sadece ihtiyaç olduğunda yükle
}: PrintReceiptServiceProps): PrintReceiptServiceReturn => {
  const { userDetails } = useAuth();
  const [screenSettings, setScreenSettings] = useState<ScreenPrintSettings | null>(null);
  const [currentReceiptNumber, setCurrentReceiptNumber] = useState<number>(1);
  const [receiptNumberLoaded, setReceiptNumberLoaded] = useState<boolean>(false);
  
  // ✅ Son yüklenen JSON'ı takip et (sonsuz döngüyü önle)
  const lastLoadedJsonRef = useRef<string | null>(null);
  
  // ✅ YENİ: Yerel servis URL'sini screenSettings'den al
  const localServiceUrl = useMemo(() => {
    const url = externalScreenSettings?.localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;
    console.log('[PrintReceiptService] 🔌 Yerel servis URL:', url);
    return url;
  }, [externalScreenSettings?.localServiceUrl]);
  
  // ✅ SQL'den gelen ayarları kullan (eğer varsa Firebase'e gitme)
  useEffect(() => {
    const printingJson = externalScreenSettings?.PrintingSettingsJson;
    
    if (!printingJson) {
      return;
    }
    
    // Aynı JSON tekrar yüklenmesin
    if (lastLoadedJsonRef.current === printingJson) {
      return;
    }
    
    try {
      const printingSettings = typeof printingJson === 'string'
        ? JSON.parse(printingJson)
        : printingJson;
      
      console.log('[PrintReceiptService] ✅ SQL ayarları yüklendi');
      setScreenSettings(printingSettings);
      lastLoadedJsonRef.current = printingJson; // ✅ Yüklenen JSON'ı kaydet
    } catch (error: any) {
      console.error('[PrintReceiptService] SQL ayarları parse edilemedi:', error);
      if (onFetchSettingsError) {
        onFetchSettingsError(`SQL ayarları yüklenirken hata: ${error.message}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalScreenSettings?.PrintingSettingsJson]); // ✅ Sadece PrintingSettingsJson'ı izle
  
  // Ekran ayarlarını Firebase'den çek (sadece SQL ayarları yoksa - backward compatibility)
  const fetchScreenSettings = useCallback(async (): Promise<ScreenPrintSettings | null> => {
    // ✅ SQL ayarları varsa Firebase'e gitme
    if (externalScreenSettings?.PrintingSettingsJson) {
      console.log('[PrintReceiptService] SQL ayarları mevcut, Firebase\'den çekilmiyor.');
      return screenSettings;
    }
    
    if (!companyId || !screenId) {
      onFetchSettingsError?.("Şirket ID veya Ekran ID bulunamadı.");
      return null;
    }
    // Yazıcı ayarları artık SQL'de (PrintingSettingsJson). RTDB fallback'i kaldırıldı;
    // SQL ayarı yoksa ayar bulunamadı kabul edilir (geriye veri taşınmadı).
    console.warn('[PrintReceiptService] SQL yazıcı ayarı (PrintingSettingsJson) yok; RTDB fallback kaldırıldı.');
    onFetchSettingsError?.("Ekran ayarları bulunamadı.");
    return null;
  }, [companyId, screenId, externalScreenSettings, screenSettings, onFetchSettingsError]);

  // Fiş numarasını localStorage'dan yükleyen fonksiyon
  const loadReceiptNumber = useCallback(() => {
    console.log("%c[PrintReceiptService] Fiş numarası yükleniyor...", "color: blue;");
    const storedReceiptNumber = localStorage.getItem('currentReceiptNumber');
    const storedReceiptDate = localStorage.getItem('lastReceiptDate');
    const todayDate = new Date().toISOString().split('T')[0];
    
    if (storedReceiptNumber) {
      const numericValue = parseInt(storedReceiptNumber, 10);
      setCurrentReceiptNumber(numericValue);
      console.log("%c[PrintReceiptService] localStorage'dan yüklenen fiş numarası:", "color: blue;", numericValue);
    } else {
      // Fiş numarası yoksa 1'den başlat
      setCurrentReceiptNumber(1);
      localStorage.setItem('currentReceiptNumber', '1');
      localStorage.setItem('lastReceiptDate', todayDate);
      console.log("%c[PrintReceiptService] Fiş numarası bulunamadı, 1 olarak ayarlandı", "color: blue;");
    }
    
    setReceiptNumberLoaded(true);
    return Promise.resolve();
  }, []);

  // Fiş numarası yönetimi için hook
  useEffect(() => {
    if (screenSettings && screenSettings.receiptDisplaySettings && screenSettings.receiptDisplaySettings.showReceiptNumber) {
      // Kayıtlı fiş numarasını storage'dan al
      const storedReceiptNumber = localStorage.getItem('currentReceiptNumber');
      const storedReceiptDate = localStorage.getItem('lastReceiptDate');
      const todayDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD formatında bugünün tarihi
      
      // Eğer gün sonunda sıfırlama seçeneği aktifse ve tarih değiştiyse
      if (screenSettings.receiptDisplaySettings.resetAtEndOfDay && storedReceiptDate && storedReceiptDate !== todayDate) {
        // Tarih değişmişse fiş numarasını 1'den başlat
        setCurrentReceiptNumber(1);
        localStorage.setItem('currentReceiptNumber', '1');
        localStorage.setItem('lastReceiptDate', todayDate);
        setReceiptNumberLoaded(true);
      } else if (storedReceiptNumber) {
        // Tarih değişmemişse ve kayıtlı numara varsa, onu kullan
        setCurrentReceiptNumber(parseInt(storedReceiptNumber, 10));
        setReceiptNumberLoaded(true);
      } else {
        // İlk çalıştırma veya kayıtlı numara yoksa, 1'den başlat ve tarihi kaydet
        setCurrentReceiptNumber(1);
        localStorage.setItem('currentReceiptNumber', '1');
        localStorage.setItem('lastReceiptDate', todayDate);
        setReceiptNumberLoaded(true);
      }
    }
  }, [screenSettings]);

  // Başlangıçta fiş numarasını yükle
  useEffect(() => {
    loadReceiptNumber();
  }, [loadReceiptNumber]);

  // Sonraki fiş numarasını al ve kaydet
  const getNextReceiptNumber = useCallback(() => {
    // ÖNEMLİ: Değişiklik - screenSettings bağımlılığını azaltıyoruz
    // Doğrudan localStorage'dan mevcut değeri oku - güvenilir olması için
    const storedReceiptNumber = localStorage.getItem('currentReceiptNumber');
    // localStorage'dan alınan değeri kullan, mevcut state değerini değil
    const currentNumber = storedReceiptNumber ? parseInt(storedReceiptNumber, 10) : currentReceiptNumber;
    
    // Şimdi yalnızca resetValue için screenSettings kullanılıyor
    const resetValue = screenSettings?.receiptDisplaySettings?.receiptNumberResetValue || 999999;
    
    // Sonraki numara için hesaplama
    let newReceiptNumber = currentNumber + 1;
    
    // Eğer maximum değeri aştıysa sıfırla
    if (newReceiptNumber > resetValue) {
      newReceiptNumber = 1;
    }
    
    // Yeni fiş numarasını localStorage'a kaydet
    localStorage.setItem('currentReceiptNumber', newReceiptNumber.toString()); // Sonraki fişin numarasını localStorage'a kaydet
    
    // Bugünün tarihini kaydet (gün sonunda sıfırlama için)
    const todayDate = new Date().toISOString().split('T')[0];
    localStorage.setItem('lastReceiptDate', todayDate);
    
    console.log("%c[PrintReceiptService] Fiş numarası kullanılıyor:", "color: green;", currentNumber);
    console.log("%c[PrintReceiptService] Sonraki fiş numarası kaydedildi:", "color: green;", newReceiptNumber);
    
    // Mevcut numarayı döndür (yani şu anki fiş için kullanılacak numara)
    return currentNumber;
  }, [screenSettings, currentReceiptNumber]);

  // İlk yüklendiğinde ayarları çek (lazyLoad=false ise)
  useEffect(() => {
    if (!lazyLoad) {
      fetchScreenSettings().catch(err => {
        console.error("Yazdırma ayarları otomatik yüklenirken hata:", err);
      });
    }
  }, [fetchScreenSettings, lazyLoad]);

  // Depo/Stok sekmesindeki ürün→ekran kategori eşlemesi (restoran mutfak ile aynı mantık)
  const buildProductScreenCategoryLookup = useCallback((): Record<string, string> => {
    const lookup: Record<string, string> = {};
    const allProductSettings = externalScreenSettings?.stockVisibility?.products || {};
    for (const warehouseId of Object.keys(allProductSettings)) {
      const warehouseProducts = allProductSettings[warehouseId];
      if (warehouseProducts && typeof warehouseProducts === 'object') {
        for (const catId of Object.keys(warehouseProducts)) {
          const productIds = warehouseProducts[catId];
          if (Array.isArray(productIds)) {
            for (const pid of productIds) {
              if (pid && !lookup[pid]) {
                lookup[pid] = catId;
              }
            }
          }
        }
      }
    }
    return lookup;
  }, [externalScreenSettings?.stockVisibility?.products]);

  // Kategori bazlı yazdırma: önce ekran ayarı kategorisi, sonra sepetteki categoryId
  const getCategoryIdsForItems = useCallback((items: CartItem[]): { [productId: string]: string } => {
    const productCategoryLookup = buildProductScreenCategoryLookup();
    const result: { [productId: string]: string } = {};
    for (const item of items) {
      const resolved =
        productCategoryLookup[item.id] || item.categoryId || undefined;
      if (resolved) {
        result[item.id] = resolved;
      }
    }
    return result;
  }, [buildProductScreenCategoryLookup]);

  // Makbuz yazdırma fonksiyonu
  const printReceipt = useCallback(async (
    baseReceiptData: Omit<ReceiptData, 'cashierName' | 'companyInfo' | 'showReceiptHeader' | 'showVatInfo' | 'showDiscountInfo' | 'productGrouping' | 'standardFooterNote' | 'thankYouMessage' | 'receiptFormat' | 'items' | 'vatTotal'>,
    cartItemsInput: CartItem[],
    /**
     * ✅ YENİ: Yazdırma davranışı opsiyonları
     * - forceSinglePrinter=true: Kategori bazlı modda OLSA BİLE, ürünleri kategoriye göre
     *   ayırıp mutfak yazıcılarına dağıtmaz; tek bir adisyon/fiş yazıcısına basar.
     *   Bu özellikle ÖDEME FİŞİ (printReceiptAfterPayment) için kritik —
     *   ödeme fişi her zaman tek bir yazıcıya (adisyon/varsayılan) gitmelidir,
     *   sepetteki ürünlerin kategorileri mutfak yazıcısına atanmış olsa bile.
     */
    options?: { forceSinglePrinter?: boolean }
  ) => {
    let settings = screenSettings;
    if (!settings) {
      settings = await fetchScreenSettings();
      if (!settings) { onPrintError?.('Yazdırma ayarları yüklenemedi'); return; }
    }

    const rds = settings.receiptDisplaySettings;
    
    // Ayarları debug için konsola yazdır
    console.log("%c[PrintReceiptService] Fiş ayarları:", "color: blue;", rds);
    console.log("%c[PrintReceiptService] Kasiyer adı göster ayarı:", "color: blue;", rds?.showCashierName);
    console.log("%c[PrintReceiptService] KDV göster ayarı:", "color: blue;", rds?.showVat);
    
    // Fiş numarası gösterilecekse ve fiş numarası yüklenmemişse, önce yükle
    if (rds?.showReceiptNumber && !receiptNumberLoaded) {
      console.log("%c[PrintReceiptService] Fiş numarası yüklenmemiş, önce yükleniyor...", "color: orange;");
      await loadReceiptNumber();
    }
    
    // Fiş numarası kullanılacaksa, numaranın yüklendiğinden emin ol
    if (rds?.showReceiptNumber && !receiptNumberLoaded) {
      console.error("%c[PrintReceiptService] Fiş numarası yüklenemedi!", "color: red;");
      onPrintError?.('Fiş numarası yüklenemedi, lütfen tekrar deneyin.');
      return;
    }

    // constructFullReceiptData fonksiyonu artık CartItem[] alacak ve KDV/Kasiyer bilgileri kesinlikle gönderilmeyecek
    const constructFullReceiptData = (itemsToProcess: CartItem[]): ReceiptData => {
        // ✅ DEBUG: Gelen sepet verilerini logla
        console.log('📋 [FİŞ DEBUG] constructFullReceiptData çağrıldı, ürün sayısı:', itemsToProcess.length);
        itemsToProcess.forEach((item, index) => {
          console.log(`📋 [FİŞ DEBUG] Ürün ${index + 1}:`, {
            name: item.name,
            hasMenu: item.hasMenu,
            hasRecipe: item.hasRecipe,
            selectedOptionsCount: item.selectedOptions?.length || 0,
            menuNormalItemsCount: item.menuNormalItems?.length || 0,
            selectedOptions: item.selectedOptions,
            menuNormalItems: item.menuNormalItems
          });
        });
        
        // Varsayılan KDV değeri (total için, görünür olmayacak)
        let calculatedVatTotal = 0;

        const transformedItems: ReceiptDataItem[] = itemsToProcess.map(item => {
            const itemTotalPrice = item.price * item.quantity;
            // Eğer KDV oranı yoksa, default KDV oranı 18 olarak kabul et (Türkiye'deki genel KDV oranı)
            const vatRate = typeof item.vatRate === 'number' ? item.vatRate : 18;
            
            // ✅ DEBUG: Item bilgilerini logla
            console.log('🎫 [FİŞ ITEM]', item.name, '- hasMenu:', item.hasMenu, 
              '- selectedOptions:', item.selectedOptions?.length || 0,
              '- menuNormalItems:', item.menuNormalItems?.length || 0);
            
            // ✅ YENİ: Alt ürünleri oluştur (menü öğeleri ve opsiyonlar)
            const subItems: ReceiptSubItem[] = [];
            
            // Menülü ürün mü kontrol et
            if (item.hasMenu) {
              // 1️⃣ Normal menü öğelerini ekle
              if (item.menuNormalItems && item.menuNormalItems.length > 0) {
                item.menuNormalItems.forEach(normalItem => {
                  subItems.push({
                    name: normalItem.itemName,
                    quantity: normalItem.quantity || 1,
                    isExtraOption: false
                  });
                });
              }
              
              // 2️⃣ Seçilen opsiyonları ekle
              if (item.selectedOptions && item.selectedOptions.length > 0) {
                item.selectedOptions.forEach(option => {
                  // ⚠️ KRİTİK: Ekstra opsiyon seçildiyse SADECE ekstra opsiyonu göster
                  // Orijinal opsiyonu GÖSTERME (stoktan da düşülmüyor çünkü)
                  if (option.selectedExtraOption) {
                    // Ekstra opsiyon var → SADECE ekstra opsiyonu ekle
                    subItems.push({
                      name: option.selectedExtraOption.optionItemName,
                      quantity: 1,
                      isExtraOption: true
                    });
                    console.log(`🎯 [FİŞ] Ekstra opsiyon eklendi: ${option.selectedExtraOption.optionItemName} (orijinal opsiyon "${option.itemName}" atlandı)`);
                  } else {
                    // Ekstra opsiyon yok → Normal opsiyonu ekle
                    subItems.push({
                      name: option.itemName,
                      quantity: 1,
                      isExtraOption: false
                    });
                  }
                });
              }
            }
            
            const itemModel: ReceiptDataItem = {
                name: item.name,
                quantity: item.quantity,
                unitPrice: item.price,
                totalPrice: itemTotalPrice,
                vatRate,
                subItems: subItems.length > 0 ? subItems : undefined  // ✅ YENİ: Alt ürünleri ekle
            };
            
            // KDV toplamını hesapla - hesaplama mantığını düzelttim ve her durumda KDV hesapla
            // KDV tutarı = KDV hariç tutar * (KDV oranı / 100)
            calculatedVatTotal += (itemTotalPrice * (vatRate / 100));
            
            return itemModel;
        });
        
        // Fiş numarası ayarlarını kontrol et ve gerekirse fiş numarası oluştur
        let receiptNumber: number | undefined = undefined;
        if (rds?.showReceiptNumber) {
            // ÖNEMLİ: Değişiklik - Fiş numarası kontrolü ekledik
            // Eğer fiş numarası yüklenmemiş veya 0/undefined ise, yükle ve kullan
            if (!receiptNumberLoaded || currentReceiptNumber <= 0) {
                console.log("%c[PrintReceiptService] Fiş numarası yükleniyor (yapılandırma sırasında)...", "color: orange;");
                // localStorage'dan doğrudan oku
                const storedNumber = localStorage.getItem('currentReceiptNumber');
                if (storedNumber) {
                    receiptNumber = parseInt(storedNumber, 10);
                    // State'i de güncelle
                    setCurrentReceiptNumber(receiptNumber);
                } else {
                    receiptNumber = 1; // Varsayılan değer
                    setCurrentReceiptNumber(1);
                    localStorage.setItem('currentReceiptNumber', '1');
                }
            } else {
                // getNextReceiptNumber fonksiyonu artık mevcut numarayı döndürüyor
                receiptNumber = getNextReceiptNumber();
            }
            
            console.log("%c[PrintReceiptService] Fiş Numarası:", "color: green;", receiptNumber);
        }

        // Temel fiş verisi oluştur
        const baseFullData: Partial<ReceiptData> = {
            ...baseReceiptData,
            items: transformedItems,
            showDiscountInfo: rds?.showDiscountInfo,
            productGrouping: rds?.productGrouping,
            standardFooterNote: rds?.standardFooterNote,
            thankYouMessage: rds?.thankYouMessage,
            resetAtEndOfDay: rds?.resetAtEndOfDay,
            printCopyCount: rds?.printCopyCount || 1, // Varsayılan olarak 1 kopya
            receiptFormat: rds ? {
                receiptWidth: rds.receiptWidth,
                characterLength: rds.characterLength,
                marginTop: rds.marginTop,
                marginBottom: rds.marginBottom,
                marginLeft: rds.marginLeft,
                marginRight: rds.marginRight,
            } : undefined,
        };
        
        // Şirket bilgisi ekle/kaldır
        // ✅ PR2 (Invoice Seller Snapshot Refactor): Franchise şubeler için fiş başlığı kritik.
        // Kullanıcı `receiptDisplaySettings`'i özellikle doldurduysa onu kullan (kullanıcı tercihi);
        // boş ise backend'den gelen seller snapshot'a fallback yap. Bu sayede normal şubelerde
        // ana firma bilgileri, franchise şubelerde franchise firmanın bilgileri görünür.
        if (rds?.showReceiptHeader && rds) {
            const fallbackName = (seller?.sellerCompanyName ?? '').trim();
            const fallbackAddress = (seller?.sellerAddress ?? '').trim();
            const fallbackTax = (seller?.sellerTaxNumber ?? '').trim();
            const fallbackPhone = (seller?.sellerPhone ?? '').trim();

            const rdsName = (rds.companyName ?? '').trim();
            const rdsAddress = (rds.companyAddress ?? '').trim();
            const rdsTax = (rds.taxNumber ?? '').trim();

            baseFullData.companyInfo = {
                name: rdsName !== '' ? rdsName : fallbackName,
                address: rdsAddress !== '' ? rdsAddress : fallbackAddress,
                taxNumber: rdsTax !== '' ? rdsTax : fallbackTax,
                phone: fallbackPhone, // rds'de phone alanı yok; her zaman seller'dan dolduruluyor
            };
            if (seller?.isFranchiseInvoice) {
                console.log(
                    '%c[PrintReceiptService] 🏪 Franchise fiş başlığı kullanılıyor:',
                    'color: orange; font-weight: bold;',
                    baseFullData.companyInfo,
                );
            }
            baseFullData.showReceiptHeader = true;
            
            // Logo ayarları (boyutlar frontend'de tanımlı)
            baseFullData.headerType = rds.headerType || 'text';
            baseFullData.logoUrl = rds.logoUrl || '';
            baseFullData.logoMaxWidthPx = rds.logoMaxWidthPx ?? 384;
            baseFullData.logoMaxHeightPx = rds.logoMaxHeightPx ?? 150;
        }
        
        // Fiş numarası varsa ekle
        if (receiptNumber !== undefined) {
            baseFullData.receiptNumber = receiptNumber;
            baseFullData.showReceiptNumber = true;
        }
        
        // Kasiyer adı boş olarak ayarla (tamamen kaldırma)
        if (rds?.showCashierName === true && userDetails) {
            baseFullData.cashierName = `${userDetails.firstName} ${userDetails.lastName}`.trim() || 'Bilinmiyor';
        } else {
            // Boş değer gönder, API için gerekli olabilir
            baseFullData.cashierName = '';
        }
        
        // KDV bilgisi boş olarak ayarla (tamamen kaldırma)
        if (rds?.showVat === true) {
            // KDV toplamı her zaman hesaplanan değer olmalı
            baseFullData.vatTotal = calculatedVatTotal;
            baseFullData.showVatInfo = true;
        } else {
            // Değeri artık 0 olarak GÖNDERMİYORUZ, doğru değeri gönderiyoruz
            baseFullData.vatTotal = calculatedVatTotal; // Burayı değiştirdim, her zaman hesaplanan KDV değerini gönder
            baseFullData.showVatInfo = true; // Burayı da değiştirdim, her zaman göster
        }
        
        // Debug için console.log ekleyelim
        console.log("%c[PrintReceiptService] Hesaplanan KDV Tutarı:", "color: orange;", calculatedVatTotal);
        console.log("%c[PrintReceiptService] vatTotal değeri:", "color: orange;", baseFullData.vatTotal);
        console.log("%c[PrintReceiptService] showVatInfo değeri:", "color: orange;", baseFullData.showVatInfo);
        
        // Tip dönüşümü yaparak nihai veriyi oluştur (eksik alanlar TypeScript tarafından uyarı vermeyecek)
        const fullData = baseFullData as ReceiptData;
        
        // HTML şablonunu oluştur ve fullData'ya ekle
        fullData.htmlTemplate = createReceiptHtml(fullData);
        
        // Debug için oluşturulan veriyi kontrol et
        console.log("%c[PrintReceiptService] Fiş verisi oluşturuldu:", "color: green;");
        console.log("→ Kasiyer adı var mı?:", 'cashierName' in fullData);
        console.log("→ KDV var mı?:", 'vatTotal' in fullData && fullData.vatTotal! > 0);
        console.log("→ HTML şablonu oluşturuldu mu?:", 'htmlTemplate' in fullData && fullData.htmlTemplate!.length > 0);
        
        return fullData;
    };

    // ✅ YENİ: forceSinglePrinter=true → kategori bazlı mod aktif olsa BİLE
    // ödeme fişini tek bir yazıcıya bas (adisyon yazıcısı varsa onu, yoksa
    // varsayılan fiş yazıcısını). Bu sayede ödeme fişi mutfak yazıcılarına
    // dağılmaz; her zaman doğru fiş yazıcısına çıkar.
    const useSingleFlow = settings.printingMode === 'single' || options?.forceSinglePrinter === true;

    if (useSingleFlow) {
      // Yazıcı önceliği (FALLBACK ZİNCİRİ):
      // - Tek yazıcı modu (printingMode==='single'): defaultReceiptPrinter
      // - Kategori modu + forceSinglePrinter:
      //     1) adisyonPrinter
      //     2) defaultReceiptPrinter
      //     3) definedPrinters[0]                                  (acil fallback)
      //     4) categoryPrinterAssignments'taki ilk yazıcı           (son çare)
      // Hiçbiri yoksa hata. Bu fallback'ler ödeme fişinin
      // KESİNLİKLE TEK BİR YAZICIYA basılmasını garanti eder
      // (kategoriye göre mutfak yazıcılarına dağılmaz).
      const adisyonPrinter = (settings as any)?.adisyonPrinter;
      let printerToUse: any =
        settings.printingMode === 'single'
          ? settings.defaultReceiptPrinter
          : (adisyonPrinter || settings.defaultReceiptPrinter);

      // Fallback 3: definedPrinters[0]
      if (!printerToUse && Array.isArray(settings.definedPrinters) && settings.definedPrinters.length > 0) {
        printerToUse = settings.definedPrinters[0];
        console.warn(
          "%c[PrintReceiptService] ⚠️ Adisyon/Varsayılan yazıcı yok. definedPrinters[0] fallback kullanılıyor:",
          "color: orange;",
          printerToUse?.name
        );
      }

      // Fallback 4: categoryPrinterAssignments'taki ilk yazıcı (printerId)
      if (!printerToUse && settings.categoryPrinterAssignments) {
        const firstAssignedId = Object.values(settings.categoryPrinterAssignments).find((id) => !!id);
        if (firstAssignedId) {
          printerToUse = { id: firstAssignedId, name: firstAssignedId };
          console.warn(
            "%c[PrintReceiptService] ⚠️ Hiç tanımlı yazıcı yok. Kategori atamalarındaki ilk yazıcı fallback kullanılıyor:",
            "color: orange;",
            firstAssignedId
          );
        }
      }

      if (!printerToUse) {
        onPrintError?.('Fiş yazıcısı (adisyon/varsayılan) ayarlanmamış');
        return;
      }

      const fullReceiptData = constructFullReceiptData(cartItemsInput);
      try {
        console.log("%c[PrintReceiptService] ✅ Tek yazıcıya yazdırma isteği gönderiliyor (forceSinglePrinter):", "color: green; font-weight: bold;", {
          mode: settings.printingMode,
          forceSinglePrinter: !!options?.forceSinglePrinter,
          printerId: printerToUse.id,
          printerName: printerToUse.name,
          itemCount: cartItemsInput.length,
          adisyonPrinterAyarli: !!adisyonPrinter,
          defaultReceiptPrinterAyarli: !!settings.defaultReceiptPrinter,
        });

        await sendPrintReceipt({
          printerId: printerToUse.id,
          printerName: printerToUse.name,
          receiptData: fullReceiptData
        }, { baseUrl: localServiceUrl });
        onPrintSuccess?.(`Fiş başarıyla yazdırıldı`);
      } catch (error: any) {
        // 🔍 Backend (local-service) 500 dönüyorsa gerçek mesaj burada:
        const backendError = error?.response?.data?.error;
        const backendDetails = error?.response?.data?.details;
        const backendStatus = error?.response?.status;
        console.error("%c[PrintReceiptService] Yazdırma hatası:", "color: red;", {
          axiosMessage: error?.message,
          backendStatus,
          backendError,
          backendDetails,
          fullResponseData: error?.response?.data,
          // Hangi receiptData ile patladı — debugging için
          payloadPreview: {
            printerName: printerToUse.name,
            itemCount: fullReceiptData?.items?.length,
            hasHeaderType: !!(fullReceiptData as any)?.headerType,
            hasLogoUrl: !!(fullReceiptData as any)?.logoUrl,
            hasInvoiceNumber: !!(fullReceiptData as any)?.invoiceNumber,
            paymentMethod: (fullReceiptData as any)?.paymentMethod,
          },
          rawError: error,
        });
        // Kullanıcıya net mesaj ver — backend mesajı varsa onu göster
        const displayMsg = backendDetails || backendError || error?.message || 'Bilinmeyen hata';
        onPrintError?.(`Fiş yazdırılırken hata oluştu: ${displayMsg}`);
      }
    } else {
      try {
        console.log('%c[PrintReceiptService] Kategori bazlı yazdırma modu aktif', 'color: blue; font-weight: bold;');
        console.log('[PrintReceiptService] Sepetteki ürün sayısı:', cartItemsInput.length);
        console.log('[PrintReceiptService] categoryPrinterAssignments:', settings?.categoryPrinterAssignments);
        console.log('[PrintReceiptService] definedPrinters:', settings?.definedPrinters);
        console.log('[PrintReceiptService] defaultReceiptPrinter:', settings?.defaultReceiptPrinter);
        
        const itemCategoryMap = getCategoryIdsForItems(cartItemsInput);
        console.log('[PrintReceiptService] Ürün-Kategori eşlemesi:', itemCategoryMap);
        
        // Yazıcı bazlı gruplama: aynı yazıcıya atanan kategorilerdeki ürünler tek fişte birleştirilir
        const printerItemsMap: { [printerId: string]: CartItem[] } = {};
        const defaultItems: CartItem[] = [];
        
        cartItemsInput.forEach(item => {
          const categoryId = itemCategoryMap[item.id];
          const printerId = categoryId ? settings?.categoryPrinterAssignments?.[categoryId] : null;
          // ✅ YENİ: Kategoriye 2. yazıcı atanmışsa fiş her iki yazıcıya da gider
          const printerId2 = categoryId ? settings?.categoryPrinterAssignments2?.[categoryId] : null;

          if (printerId || printerId2) {
            if (printerId) {
              if (!printerItemsMap[printerId]) printerItemsMap[printerId] = [];
              printerItemsMap[printerId].push(item);
            }
            if (printerId2 && printerId2 !== printerId) {
              if (!printerItemsMap[printerId2]) printerItemsMap[printerId2] = [];
              printerItemsMap[printerId2].push(item);
            }
          } else {
            defaultItems.push(item);
          }
        });
        
        console.log('[PrintReceiptService] Kategori bazlı dağılım:', {
          kategoriliUrunSayisi: Object.values(printerItemsMap).reduce((sum, items) => sum + items.length, 0),
          varsayilanUrunSayisi: defaultItems.length,
          yaziciSayisi: Object.keys(printerItemsMap).length
        });
        
        const printPromises: Promise<any>[] = [];
        
        Object.entries(printerItemsMap).forEach(([printerId, itemsForPrinter]) => {
          const printer = settings?.definedPrinters?.find(p => p.id === printerId);
          const printerName = printer?.name || printerId;
          const printerReceiptData = constructFullReceiptData(itemsForPrinter);
          console.log(`%c[PrintReceiptService] Yazıcıya yazdırma isteği gönderiliyor:`, "color: green;", {
            printerId,
            printerName,
            itemCount: itemsForPrinter.length
          });
          
          printPromises.push(sendPrintReceipt({
              printerId: printerId,
              printerName: printerName,
              receiptData: printerReceiptData
          }, { baseUrl: localServiceUrl }));
        });
        
        const adisyonPrinter = (settings as any)?.adisyonPrinter;
        const uncategorizedPrinter = (settings as any)?.uncategorizedPrinter;

        // 1) Kategorisiz ürünler → "Kategorisiz Ürün Yazıcısı" (kendi mutfak fişi).
        //    - Seçili değilse: adisyon yazıcısı da yoksa varsayılan fiş yazıcısına düş (ürünler kaybolmasın).
        //    - Adisyon yazıcısı varsa: kategorisiz ürünler zaten tam adisyonda görüneceğinden
        //      ayrı yazıcı yoksa tekrar basılmaz.
        //    - Kategorisiz yazıcısı = adisyon yazıcısı ise: tam adisyonda zaten var, ayrı basılmaz.
        if (defaultItems.length > 0) {
          const sameAsAdisyon = !!uncategorizedPrinter && !!adisyonPrinter && uncategorizedPrinter.id === adisyonPrinter.id;
          const uncatTarget = sameAsAdisyon
            ? null
            : (uncategorizedPrinter || (!adisyonPrinter ? settings?.defaultReceiptPrinter : null));

          if (uncatTarget) {
            const defaultReceiptData = constructFullReceiptData(defaultItems);
            console.log(`%c[PrintReceiptService] Kategorisiz ürünler '${uncatTarget.name}' yazıcısına gönderiliyor:`, "color: green;", {
              printerId: uncatTarget.id,
              printerName: uncatTarget.name,
              itemCount: defaultItems.length
            });

            printPromises.push(sendPrintReceipt({
                printerId: uncatTarget.id,
                printerName: uncatTarget.name,
                receiptData: defaultReceiptData
            }, { baseUrl: localServiceUrl }));
          } else if (!adisyonPrinter && !sameAsAdisyon) {
            console.warn(`%c[PrintReceiptService] ${defaultItems.length} kategorisiz ürün için yazıcı yok (kategorisiz/adisyon/varsayılan) - bu ürünler yazdırılmayacak!`, 'color: orange; font-weight: bold;');
          }
        }

        // 2) Adisyon (TÜM ürünler) → kasa/adisyon yazıcısı (müşteri fişi).
        //    Kategori modunda da kasadaki yazıcıdan TAM fiş çıkar; kategori fişleri mutfağa,
        //    tam adisyon kasaya gider.
        if (adisyonPrinter) {
          const fullAdisyonData = constructFullReceiptData(cartItemsInput);
          console.log(`%c[PrintReceiptService] Tam adisyon (tüm ürünler) '${adisyonPrinter.name}' yazıcısına gönderiliyor:`, "color: green; font-weight: bold;", {
            printerId: adisyonPrinter.id,
            printerName: adisyonPrinter.name,
            itemCount: cartItemsInput.length
          });

          printPromises.push(sendPrintReceipt({
              printerId: adisyonPrinter.id,
              printerName: adisyonPrinter.name,
              receiptData: fullAdisyonData
          }, { baseUrl: localServiceUrl }));
        }
        
        if (printPromises.length > 0) {
          await Promise.all(printPromises);
          onPrintSuccess?.(`Tüm fişler başarıyla yazdırıldı`);
        }
        else { onPrintError?.('Yazdırılacak fiş bulunamadı'); }
      } catch (error: any) {
        console.error("%c[PrintReceiptService] Yazdırma hatası:", "color: red;", error);
        onPrintError?.(`Fiş yazdırılırken hata oluştu: ${error.message}`);
      }
    }
  }, [screenSettings, companyId, onPrintError, onPrintSuccess, fetchScreenSettings, getCategoryIdsForItems, userDetails, receiptNumberLoaded, loadReceiptNumber, seller]);

  // Dışarıdan erişim için yazdırma fonksiyonunu döndür
  return { printReceipt };
};

// Geriye uyumluluk için ayrıca bir bileşen olarak da dışa aktarabiliriz
const PrintReceiptService: React.FC<PrintReceiptServiceProps & { children?: React.ReactNode }> = (props) => {
  // Bu bileşen sadece hook'u saracak ve hiçbir şey render etmeyecek
  usePrintReceipt(props);
  return null;
};

export default PrintReceiptService; 